import { useTranslations } from '@vocab/react';
import {
  Bleed,
  Box,
  Divider,
  Heading,
  IconChevron,
  Inline,
  PageBlock,
} from 'braid-design-system';
import { useEffect, useState } from 'react';

import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { isMobileUserAgent } from 'src/modules/chalice-user-agent/device-detect';
import { useSelector } from 'src/store/react';
import {
  selectAuthenticated,
  selectRecentSearches,
  selectUserAgent,
} from 'src/store/selectors';

import translations from './.vocab';
import HorizontalScrollContainer from './components/HorizontalScrollContainer';
import RecentSearchItem from './components/RecentSearchItem';

import * as styles from './RecentSearchesBar.css';

const ITEM_GAP = 'xsmall';
const HORIZONTAL_SCROLLING_PADDING = 'xxsmall';

const RecentSearchesBar = () => {
  const analyticsFacade = useAnalyticsFacade();

  const [isExpanded, setIsExpanded] = useState(false);
  const searchItems = useSelector(selectRecentSearches);
  const userAgent = useSelector(selectUserAgent);
  const isMobileOrTablet = isMobileUserAgent(userAgent);
  const isAuthenticated = useSelector(selectAuthenticated);

  const { t } = useTranslations(translations);

  useEffect(() => {
    analyticsFacade.recentSearchesImpression({
      cardDisplayedCount: `${searchItems?.length ?? 0}`,
      recentSearchesCollapsed: isMobileOrTablet,
      isExitLink: false,
    });
  }, [analyticsFacade, isMobileOrTablet, searchItems?.length]);

  if (!searchItems?.length) {
    return null;
  }

  const content = searchItems.map((item, index) =>
    item ? (
      <Box key={index} data-automation={`recent-search-item-${index}`}>
        <RecentSearchItem
          search={item}
          listIndex={index + 1}
          listDisplayCount={searchItems.length}
        />
      </Box>
    ) : null,
  );

  if (isAuthenticated) {
    return (
      <PageBlock data={{ automation: 'recent-searches-bar' }}>
        <Bleed bottom={HORIZONTAL_SCROLLING_PADDING}>
          <HorizontalScrollContainer>
            <Box
              marginTop={{ mobile: 'medium', desktop: 'gutter' }}
              className={styles.multilineItems}
              display="flex"
              paddingBottom={HORIZONTAL_SCROLLING_PADDING}
              gap={ITEM_GAP}
            >
              {content}
            </Box>
          </HorizontalScrollContainer>
        </Bleed>
      </PageBlock>
    );
  }
  return (
    <PageBlock data={{ automation: 'recent-searches-bar' }}>
      <Box
        component="button"
        textAlign="left"
        width="full"
        cursor="pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        marginTop={{ mobile: 'xlarge' }}
        display={{
          tablet: searchItems.length >= 3 ? 'block' : 'none',
          desktop: 'none',
        }}
        aria-expanded={isExpanded}
      >
        <Heading level="3">
          <Box
            component="span"
            display="flex"
            justifyContent="spaceBetween"
            alignItems="center"
          >
            {t('Recent searches')}{' '}
            <IconChevron direction={isExpanded ? 'up' : 'down'} />
          </Box>
        </Heading>
      </Box>
      <Box
        display={{
          mobile: isExpanded ? 'block' : 'none',
          desktop: 'block',
        }}
        paddingTop={{
          mobile: isExpanded ? 'medium' : 'none',
          desktop: 'gutter',
        }}
      >
        <Inline space={ITEM_GAP}>{content}</Inline>
      </Box>
      <Box display={{ tablet: 'none' }} marginTop="xlarge">
        <Divider />
      </Box>
    </PageBlock>
  );
};

export default RecentSearchesBar;
